import { useEffect, useState, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Stack,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
} from "@mui/material";
// Icons
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from '@mui/icons-material/Add';
import { pageVariants, pageTransition } from "../../constants/GlobalStyles";
import { motion } from 'framer-motion';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

const PendingEncounterDetails = ({ encounter, handleDispenseCpts, handleDone }) => {
  const [selectedCpts, setSelectedCpts] = useState([]);
  const [file, setFile] = useState(undefined);
  const fileInputRef = useRef();
  const [openDispenseDialog, setOpenDispenseDialog] = useState(false);
  const [openDoneDialog, setOpenDoneDialog] = useState(false);

  const [encounterNote, setEncounterNote] = useState("");

  const statusLabel = {
    approved: "Approved",
    rejected: "Rejected",
  }

  const handleToggleCpt = (cptObj) => {
    setSelectedCpts((preV) => {
      const foundCpt = preV.find((cpt) => cpt.encounter_cpt_id === cptObj.id);

      if (foundCpt) {
        return preV.filter((cpt) => cpt.encounter_cpt_id !== cptObj.id);
      } else {
        return [...preV, {
          "encounter_cpt_id": cptObj.id,
          "shortdescription": cptObj.shortdescription,
        }];
      }
    })

  };


  useEffect(() => {
    setSelectedCpts([]);
    setEncounterNote("");
  }, [encounter]);

  const undispensedCpts = encounter.encountercpts.filter(
    (cpt) =>
      !cpt.has_result
  )

  // CPT Note change handler
  const handleChangeCptNote = (InputEvent) => {
    const { value,  id } = InputEvent;
    setSelectedCpts((prev) => {
      return prev.map((cpt) => {
        if (cpt.encounter_cpt_id.toString() === id.toString()) {
          return {
            ...cpt,
            notes: value,
          }
        } else {
          return cpt;
        } 
      })
    });
  }

  const handleSelectFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf";

    if (isImage) {
      try {
        const convertedBlob = await convertImage(file, "image/jpeg", 0.8); // convert to JPEG
        const convertedFile = new File([convertedBlob], file.name.split('.')[0] + ".jpg", {
          type: "image/jpeg",
        });
        setFile(convertedFile); // converted image
      } catch (err) {
        console.error("Image conversion error:", err);
        setFile(file); // store original img if conversion fails
      }
    } else if (isPdf) {
      setFile(file); // pdf still original
    } else {
      console.warn("Unsupported file type");
    }
  };

  const convertImage = (file, targetType = "image/jpeg", quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.src = e.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          canvas.toBlob(
            (blob) => {
              if (blob) resolve(blob);
              else reject(new Error("Conversion failed"));
            },
            targetType,
            quality
          );
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

    const handleDelete = (e) => {
    e.stopPropagation(); // prevent re-opening file picker
    setFile(null);
    fileInputRef.current.value = null;
  };

    const getFileIcon = () => {
    if (!file) return <UploadFileIcon sx={{ fontSize: 40 }} />;
    if (file.type === "application/pdf")
      return <PictureAsPdfIcon sx={{ fontSize: 40, color: "#d32f2f" }} />;
    return <ImageIcon sx={{ fontSize: 40, color: "#1976d2" }} />;
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={pageTransition}
      key={encounter.id}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: "8px",
          border: "1px solid #e5e7eb",
          overflow: "hidden",
          fontFamily: "'Inter', sans-serif"
        }}
      >
        {/* HEADER SECTION */}
        <Box sx={{ p: 3, bgcolor: "#f0fdfa" }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: "#111827" }}>
            {encounter.patient.name}
          </Typography>
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarTodayIcon sx={{ fontSize: 18, color: "#10b981" }} />
              <Typography fontWeight={500} fontSize={16} color="#4b5563">
                {new Date(encounter.date).toLocaleString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                }).replace(",", " •")}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonOutlineIcon sx={{ fontSize: 18, color: "#10b981" }} />
              <Typography fontWeight={500} fontSize={16} color="#4b5563">
                Dr. {encounter.provider.name}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentOutlinedIcon sx={{ fontSize: 18, color: "#10b981" }} />
              <Typography fontSize={16} fontWeight={500} color="#4b5563">
                Prescription ID: {encounter.reference_id}
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* ICDs section */}
        <Box sx={{ p: 1, borderBottom: '1px solid #f3f4f6' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1, }}>
            <LocalHospitalIcon sx={{ fontSize: 20, color: "#64748B" }} />
            <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#475569", letterSpacing: '0.025em' }}>
              DIAGNOSIS (ICD CODES)
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {encounter.encountericds?.map((icd) => (
              <Grid item xs={12} sm={6} key={icd.id}>
                <Box sx={{ 
                  py: 1, 
                  px: 3, 
                  border: '1px solid #f1f5f9', 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  bgcolor: '#FBFDFF'
                }}>
                  <Box>
                    <Typography component="div" sx={{fontWeight: 600, fontSize: "0.85rem", color: "#64748B", letterSpacing: '0.05em' }}>
                      {icd.icd} {icd.primary && <Chip label="Primary" size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem', bgcolor: '#e0f2fe', color: '#0369a1' }} />}
                    </Typography>
                    <Typography sx={{ color: "#64748b", fontSize: "0.85rem" }}>
                      {icd.shortdescription}
                    </Typography>
                  </Box>
                  <Chip
                    label={icd.actionmsg?.status?.state || 'Pending'}
                    size="small"
                    sx={{ 
                      fontWeight: 600, 
                      textTransform: 'capitalize',
                      bgcolor: icd.actionmsg?.status?.state === 'approved' ? "#dcfce7" : '#fee2e2', 
                      color: icd.actionmsg?.status?.state === 'approved' ? "#166534" : '#b91c1c' 
                    }}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* DRUGs section */}
        {/* COLUMN HEADERS */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mx: 1, my: 1.5 }}>
          <MedicalInformationIcon sx={{ fontSize: 20, color: "#64748B" }} />
          <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", color: "#475569", letterSpacing: '0.025em' }}>
            PROCEDURES
          </Typography>
        </Box>
        
        {/* HEADER SECTION - Added pr: 5.5 to match the Accordion's expand icon space */}
        <Box sx={{ m: 1, px: 3, py: 2, pr: 5.5, borderBottom: '1px solid #f3f4f6', bgcolor: '#F8FAFC' }}>
          <Grid container sx={{ fontWeight: 600, fontSize: "0.85rem", color: "#64748B", letterSpacing: '0.05em' }}>
            <Grid item xs={1.5} sx={{ textAlign: "center" }}>UPLOAD</Grid>
            <Grid item xs={4.5} sx={{ textAlign: "center" }}>DESCRIPTION</Grid>
            <Grid item xs={2} sx={{ textAlign: "center" }}>HAS RESULT</Grid>
            <Grid item xs={2} sx={{ textAlign: "center" }}>COST</Grid>
            <Grid item xs={2} sx={{ textAlign: "center" }}>INSURANCE STATUS</Grid>
          </Grid>
        </Box>

        {/* DRUG LIST */}
        <Box >
            {encounter.encountercpts.map((cpt) => (
              <Grid key={cpt.cpt_code} container alignItems="center" sx={{ m: 1, px: 3, py: 2, pr: 5.5, borderBottom: '1px solid #f3f4f6' }}>
              <Grid item xs={1.5} sx={{ display: "flex", justifyContent: "center" }}>
                <Checkbox
                  checked={selectedCpts.some(d => d.encounter_cpt_id === cpt.id)}
                  disabled={cpt.has_result}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleToggleCpt(cpt);
                  }}
                  sx={{ color: "#10b981", "&.Mui-checked": { color: "#34d399" }, p: 0 }}
                />
              </Grid>
              <Grid item xs={4.5} sx={{ textAlign: "center" }}>
                <Typography sx={{ fontWeight: 500, color: cpt.has_result === 'Approved' ? "#4b5563" : '#E02626' }}>
                  {cpt.shortdescription}
                </Typography>
              </Grid>
              <Grid item xs={2} sx={{ display: "flex", justifyContent: "center" }}>
                <Chip
                  label={cpt.has_result ? 'Yes' : 'No'}
                  sx={{ bgcolor: "#ffffffff", color: cpt.has_result ? '#0d9488' : '#E02626', fontWeight: 600, borderRadius: "4px", height: 24 }}
                />
              </Grid>
              <Grid item xs={2} sx={{ display: "flex", justifyContent: "center" }}>
                <Chip
                  label={`$${Number(cpt.cost).toFixed(2)}`}
                  sx={{ bgcolor: "#f0fdf4", color: "#166534", fontWeight: 600, borderRadius: "4px", height: 24 }}
                />
              </Grid>

              <Grid item xs={2} sx={{ display: "flex", justifyContent: "center" }}>
                  <Chip
                    label={statusLabel[cpt?.actionmsg?.status?.state]}
                    sx={{ bgcolor: cpt?.actionmsg?.status?.state === 'approved' ? "#dcfce7" : '#FEE2E2', color: cpt?.actionmsg?.status?.state === 'approved' ? "#166534" : '#E02626', fontWeight: 600, borderRadius: "4px", height: 24 }}
                  />
              </Grid>
            </Grid>
            ))}
        </Box>
        

        {/* FOOTER */}
        <Box sx={{ p: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: '1px solid #f3f4f6' }}>
          <Typography component='div' sx={{ color: "#64748B", fontSize: "0.875rem", fontWeight: 600 }}>
            <span style={{ color: '#4ABBA7' }}>{selectedCpts.length}</span> Procedures(s) selected
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<CheckIcon />}
              // disabled={selectedCpts.length === 0}
              sx={{
                bgcolor: "#2563eb",
                textTransform: "none",
                borderRadius: "6px",
                px: 3,
                fontWeight: 600,
                width: 'fit-content',
                "&:hover": { 
                  bgcolor: "#1d4ed8"
                },
              }}
              onClick={() => setOpenDoneDialog(true)}
            >
              Done
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={selectedCpts.length === 0}
              sx={{
                bgcolor: "#45a291",
                textTransform: "none",
                borderRadius: "6px",
                px: 3,
                fontWeight: 600,
                width: 'fit-content',
                "&:hover": { bgcolor: "#388e7e" },
              }}
              onClick={() => setOpenDispenseDialog(true)}
            >
              Upload
            </Button>

          </Box>
        </Box>
      </Paper>
      <Dialog fullWidth open={openDispenseDialog} onClose={() => setOpenDispenseDialog(false)}>
        <DialogTitle>Confirm Upload</DialogTitle>

        <DialogContent>
          <Typography mb={2}>
            Are you sure you want to upload{" "}
            <b>{selectedCpts.length}</b> procedure(s)?
          </Typography>

          <Box>
            {selectedCpts.map((cpt) => {
              return <TextField
                key={cpt.encounter_cpt_id}
                label={`${cpt.shortdescription} Note`}
                placeholder={`Add note for ${cpt.shortdescription} (optional)`}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
                onChange={(e) => handleChangeCptNote({ value: e.target.value, id: cpt.encounter_cpt_id })}
              />
            })}

            <input ref={fileInputRef} hidden type='file' accept="image/*,application/pdf" onChange={handleSelectFile} />
            <Paper
              elevation={0}
              onClick={() => fileInputRef.current.click()}
              sx={{
                position: "relative",
                border: "2px dashed #cfd8dc",
                borderRadius: 3,
                p: 3,
                cursor: "pointer",
                transition: "0.2s",
                "&:hover": {
                  borderColor: "#1976d2",
                  backgroundColor: "#f5faff",
                },
                "&:hover .delete-btn": {
                  opacity: 1,
                },
                minHeight: 140,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {file && (
                <IconButton
                  size="small"
                  className="delete-btn"
                  onClick={handleDelete}
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    opacity: 0,
                    transition: "0.2s",
                    backgroundColor: "#fff",
                    boxShadow: 1,
                    "&:hover": {
                      backgroundColor: "#ffebee",
                      color: "#d32f2f",
                    },
                  }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}

              <Stack spacing={1} alignItems="center" sx={{ width: "100%" }}>
                {getFileIcon()}

                <Typography fontWeight={500}>
                  {file ? "File selected" : "Upload file"}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  PDF, PNG, JPG or JPEG
                </Typography>

                <Box
                  sx={{
                    mt: 1,
                    px: 2,
                    py: 0.5,
                    borderRadius: 2,
                    backgroundColor: "#eef4ff",
                    maxWidth: "100%",
                    visibility: file ? "visible" : "hidden",
                  }}
                >
                  <Typography variant="caption" noWrap title={file?.name}>
                    {file ? file.name : "placeholder"}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDispenseDialog(false)}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color="success"
            disabled={!file}
            onClick={() => {
              handleDispenseCpts({
                encounterID: encounter.id,
                selectedCpts,
                notes: 'General notes for all tests',
                file,
              });

              setEncounterNote("");
              setOpenDispenseDialog(false);
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Done Dialog */}

      <Dialog open={openDoneDialog} onClose={() => setOpenDispenseDialog(false)}>
        <DialogTitle>Confirm</DialogTitle>

        <DialogContent>
          <Typography mb={2}>
            Are you sure you want to complete this upload?
          </Typography>

          {undispensedCpts.length > 0 && <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography fontWeight={500}>
              The following prescribed procedures have not been uploaded:
            </Typography>

            <Box mt={1} pl={2}>
              {undispensedCpts
                .map((cpt) => (
                  <Typography
                    key={cpt.cpt_code}
                    variant="body2"
                    sx={{ color: "text.secondary" }}
                  >
                    • {cpt.shortdescription}
                  </Typography>
                ))}
            </Box>
          </Alert>}

          <TextField
            label="Encounter Note (optional)"
            placeholder={
              undispensedCpts.length > 0
                ? "Add a note explaining why some medications were not uploaded"
                : "Add a note for this encounter"
            }
            multiline
            rows={3}
            fullWidth
            value={encounterNote}
            onChange={(e) => setEncounterNote(e.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDoneDialog(false)}>
            Cancel
          </Button>

          <Button
            variant="contained"
            color="success"
            onClick={() => {
              handleDone({
                encounter_id: encounter.id,
                notes: encounterNote,
              });

              setOpenDoneDialog(false);
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>

  );
};

export default PendingEncounterDetails;